import React, { useState } from "react";

// Bundled local SVGs
import googleIcon from "../../assets/images/google.svg";
import microsoftLogo from "../../assets/images/microsoft.svg";
import dropboxLogo from "../../assets/images/dropbox.svg";
import shopifyLogo from "../../assets/images/shopify.svg";
import zoomLogo from "../../assets/images/zoom.svg";
import calendlyLogo from "../../assets/images/calendly.svg";
import slackLogo from "../../assets/images/slack.svg";
import notionLogo from "../../assets/images/notion.svg";
import bitlyLogo from "../../assets/images/bitly.svg";
import discordLogo from "../../assets/images/discord.svg";
import yahooLogo from "../../assets/images/yahoo.svg";
import postgreLogo from "../../assets/images/postgre.svg";
import mysqlLogo from "../../assets/images/mysql.svg";
import sqlLogo from "../../assets/images/sql.svg";
import gmailLogo from "../../assets/images/gmail.svg";
import emailLogo from "../../assets/images/email.svg";
import connectLogo from "../../assets/images/connect.svg";

// Mapping of normalized tool names to local bundled SVGs
const LOCAL_ICONS = {
    "google": googleIcon,
    "google authenticator": googleIcon,
    "gmail": gmailLogo,
    "microsoft": microsoftLogo,
    "microsoft app": microsoftLogo,
    "outlook": emailLogo,
    "postgresql": postgreLogo,
    "postgres": postgreLogo,
    "mysql": mysqlLogo,
    "sql server": sqlLogo,
    "sql": sqlLogo,
    "dropbox": dropboxLogo,
    "shopify": shopifyLogo,
    "zoom": zoomLogo,
    "calendly": calendlyLogo,
    "slack": slackLogo,
    "notion": notionLogo,
    "bitly": bitlyLogo,
    "discord": discordLogo,
    "yahoo": yahooLogo,
    "yahoo mail": yahooLogo,
    "yahoo app": yahooLogo,
    "pms connect": connectLogo,
    "pms": connectLogo,
};

// Known mappings to Simple Icons brand slugs
const BRAND_SLUGS = {
    "sql server": "microsoftsqlserver",
    "sql": "microsoftsqlserver",
    "google authenticator": "google",
    "microsoft app": "microsoft",
    "yahoo": "yahoo",
    "pms connect": "",
};

// Deterministic pastel color generator for initial badge
const getInitialsColor = (str) => {
    const colors = [
        { bg: "#EFF6FF", text: "#2563EB", border: "#BFDBFE" }, // blue
        { bg: "#F5F3FF", text: "#7C3AED", border: "#DDD6FE" }, // purple
        { bg: "#ECFDF5", text: "#059669", border: "#A7F3D0" }, // emerald
        { bg: "#FFF7ED", text: "#EA580C", border: "#FED7AA" }, // orange
        { bg: "#FDF2F8", text: "#DB2777", border: "#FBCFE8" }, // pink
        { bg: "#EEF2FF", text: "#4F46E5", border: "#C7D2FE" }, // indigo
        { bg: "#F0FDF4", text: "#16A34A", border: "#BBF7D0" }, // green
        { bg: "#FEF2F2", text: "#DC2626", border: "#FECACA" }, // red
    ];
    let hash = 0;
    const clean = (str || "").trim().toLowerCase();
    for (let i = 0; i < clean.length; i++) {
        hash = clean.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
};

const getInitials = (str) => {
    if (!str) return "PV";
    const words = str.trim().split(/[\s_-]+/);
    if (words.length === 1) {
        return words[0].slice(0, 2).toUpperCase();
    }
    return (words[0][0] + words[1][0]).toUpperCase();
};

/**
 * Universal Vault Type Icon Component
 * 1. Checks DB explicit icon_url / icon in details
 * 2. Checks bundled local SVGs (zero latency)
 * 3. Checks standard brand CDN (SimpleIcons) for future database tools
 * 4. Gracefully falls back to a sleek, modern letter avatar
 */
const VaultTypeIcon = ({
    type,
    details,
    size = 28,
    className = "",
    style = {},
    showBorder = true,
}) => {
    const [imgFailed, setImgFailed] = useState(false);
    const [cdnFailed, setCdnFailed] = useState(false);

    const typeName = (type || "").trim();
    const lowerName = typeName.toLowerCase();

    // 1. Check if DB details contains an icon URL
    let dbIconUrl = null;
    if (details) {
        let parsed = details;
        if (typeof details === "string") {
            try {
                parsed = JSON.parse(details);
            } catch (e) {
                parsed = null;
            }
        }
        if (parsed && typeof parsed === "object") {
            dbIconUrl = parsed.icon_url || parsed.icon || parsed.logo_url || null;
        }
    }

    // 2. Check local bundled icon
    const localIcon = LOCAL_ICONS[lowerName];

    // 3. Simple Icons CDN slug
    const cdnSlug = BRAND_SLUGS[lowerName] !== undefined
        ? BRAND_SLUGS[lowerName]
        : lowerName.replace(/[^a-z0-9]/g, "");
    const cdnUrl = cdnSlug ? `https://cdn.simpleicons.org/${cdnSlug}` : null;

    const dimension = typeof size === "number" ? `${size}px` : size;

    const baseContainerStyle = {
        width: dimension,
        height: dimension,
        minWidth: dimension,
        minHeight: dimension,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "6px",
        overflow: "hidden",
        flexShrink: 0,
        verticalAlign: "middle",
        ...style,
    };

    // Render 1: DB Icon URL
    if (dbIconUrl && !imgFailed) {
        return (
            <div
                className={`vault-type-icon-wrapper ${className}`}
                style={{
                    ...baseContainerStyle,
                    background: "#f8fafc",
                    border: showBorder ? "1px solid #e2e8f0" : "none",
                    padding: "2px",
                }}
            >
                <img
                    src={dbIconUrl}
                    alt={typeName}
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                    onError={() => setImgFailed(true)}
                />
            </div>
        );
    }

    // Render 2: Bundled Local SVG
    if (localIcon) {
        return (
            <div
                className={`vault-type-icon-wrapper ${className}`}
                style={{
                    ...baseContainerStyle,
                    background: "#ffffff",
                    border: showBorder ? "1px solid #e2e8f0" : "none",
                    padding: "3px",
                }}
            >
                <img
                    src={localIcon}
                    alt={typeName}
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
            </div>
        );
    }

    // Render 3: Brand CDN for future tools
    if (cdnUrl && !cdnFailed) {
        return (
            <div
                className={`vault-type-icon-wrapper ${className}`}
                style={{
                    ...baseContainerStyle,
                    background: "#ffffff",
                    border: showBorder ? "1px solid #e2e8f0" : "none",
                    padding: "4px",
                }}
            >
                <img
                    src={cdnUrl}
                    alt={typeName}
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                    onError={() => setCdnFailed(true)}
                />
            </div>
        );
    }

    // Render 4: Smart Letter Avatar Fallback
    const colors = getInitialsColor(typeName);
    const initials = getInitials(typeName);
    const fontSize = typeof size === "number" ? Math.max(10, Math.floor(size * 0.42)) : "11px";

    return (
        <div
            className={`vault-type-icon-wrapper vault-type-initials-badge ${className}`}
            style={{
                ...baseContainerStyle,
                backgroundColor: colors.bg,
                color: colors.text,
                border: showBorder ? `1px solid ${colors.border}` : "none",
                fontWeight: 700,
                fontSize: `${fontSize}px`,
                letterSpacing: "0.5px",
                userSelect: "none",
            }}
            title={typeName}
        >
            {initials}
        </div>
    );
};

export default VaultTypeIcon;
